import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';

const Container = styled.div`
  box-shadow: ${({ boxShadow }) => boxShadow};
  height: ${({ height, autoHeight }) => (autoHeight ? 'auto' : height)};
  /* margin: 0 auto; */
  margin: ${({ marginSet }) => marginSet || '0 auto'};
  min-height: ${({ minHeight }) => minHeight};
  min-width: ${({ minWidth }) => minWidth};
  max-height: ${({ maxHeight }) => maxHeight};
  max-width: ${({ maxWidth }) => maxWidth};
  padding-bottom: ${({ paddingBottom }) => paddingBottom};
  position: relative;
  width: ${({ width }) => width};
`;

const Filler = styled.div`
  max-height: 100%;
  max-width: 100%;
  padding-bottom: ${({ paddingBottom }) => paddingBottom};
`;

const ActualImage = styled.img`
  height: ${({ autoHeight }) => (autoHeight ? 'auto' : '100%')};
  left: 0;
  max-height: 100%;
  max-width: 100%;
  object-fit: ${({ objectFit }) => objectFit};
  object-position: ${({ objectPosition }) => objectPosition};
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  position: ${({ autoHeight }) => (autoHeight ? 'static' : 'absolute')};
  top: 0;
  transition: all 250ms ease-in-out;
  width: 100%;
  // for IE
  font-family: 'object-fit: ${({ objectFit }) => objectFit}; object-position: ${({ objectPosition }) => objectPosition};';
`;

const Placeholder = ActualImage.extend`
  filter: blur(10px);
`;

/**
 * Renders an image from Contenful progressively when provided the fields of an image asset.
 *
 * @prop {object} src            fields object from contentful response JSON: asset.fields
 * @prop {object} options        options for the image
 *   @prop {string} alt          alt text
 *   @prop {string} aspectRatio  aspect ratio
 *   @prop {string} h            height
 *   @prop {string} w            width
 *   @prop {string} objectFit    css object-fit property
 *   @prop {string} size         contentful image sizes
 */
class Image extends React.Component {
  state = {
    placeholderLoaded: false,
    imageLoaded: false,
    hasState: false,
  };

  componentDidMount() {
    this.setState({ hasState: true }); // eslint-disable-line
  }

  placeholderLoaded = () => {
    this.setState({ placeholderLoaded: true });
  };

  imageLoaded = () => {
    this.setState({ imageLoaded: true });
  };

  render() {
    // Load non-optional data
    const { placeholderLoaded, imageLoaded, hasState } = this.state;
    const { description, file, title } = this.props.src;
    const { options, screenSize, marginSet } = this.props;
    const { height, width } = file.details.image;
    const { url } = file;
    const optionsTmp = Object.assign({}, options);

    // Load optional data based on screen size
    Object.keys(options).forEach(key => {
      const option = options[key];
      if (Array.isArray(options[key])) {
        // If the array provided is not long enough, use the last availabel index
        optionsTmp[key] = screenSize < option.length ? option[screenSize] : option[option.length - 1];
      }
    });
    const {
      alt,
      aspectRatio,
      boxShadow,
      h,
      w,
      minHeight,
      minWidth,
      maxHeight,
      maxWidth,
      objectFit,
      objectPosition,
      size,
      paddingBottom,
      autoHeight,
    } = optionsTmp;

    // Calculate width and height of image to request from Contentful
    const ar = aspectRatio || width / height;
    const requestWidth = size || width;
    const requestHeight = Math.round(requestWidth / ar);

    // Construct urls for different pixel density screens
    const src = {
      x1: `${url}?h=${requestHeight}&w=${requestWidth}`,
      x2: `${url}?h=${Math.round(requestHeight * 1.5)}&w=${Math.round(requestWidth * 1.5)}`,
      x3: `${url}?h=${Math.round(requestHeight * 2)}&w=${Math.round(requestWidth * 2)}`,
      placeholder: `${url}?h=${Math.round(requestHeight / 10)}&w=${Math.round(requestWidth / 10)}`,
    };
    return (
      <Container
        height={h}
        width={w || `${requestWidth}px`}
        minHeight={minHeight || null}
        minWidth={minWidth || null}
        maxHeight={maxHeight || '100%'}
        maxWidth={maxWidth || '100%'}
        marginSet={marginSet}
        paddingBottom={paddingBottom}
        boxShadow={imageLoaded && boxShadow}
        autoHeight={autoHeight}
      >
        {!h && <Filler paddingBottom={`${100 / ar}%`} />}

        {hasState && (
          <Placeholder
            visible={!imageLoaded && placeholderLoaded}
            onLoad={this.placeholderLoaded}
            objectFit={objectFit}
            objectPosition={objectPosition}
            src={src.placeholder}
          />
        )}

        <ActualImage
          alt={alt || description || title}
          autoHeight={autoHeight}
          visible={hasState ? imageLoaded : true}
          objectFit={objectFit}
          objectPosition={objectPosition}
          onLoad={this.imageLoaded}
          srcSet={`${src.x1}, ${src.x2} 1.5x, ${src.x3} 2x`}
          src={src.x3}
        />
      </Container>
    );
  }
}

Image.propTypes = {
  src: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    file: PropTypes.shape({
      fileName: PropTypes.string.isRequired,
      contentType: PropTypes.string.isRequired,
      details: PropTypes.shape({
        image: PropTypes.shape({
          height: PropTypes.number.isRequired,
          width: PropTypes.number.isRequired,
        }).isRequired,
        size: PropTypes.number.isRequired,
      }).isRequired,
      url: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  screenSize: PropTypes.number.isRequired,

  // Optional properties
  options: PropTypes.shape({
    alt: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    aspectRatio: PropTypes.oneOfType([PropTypes.number, PropTypes.arrayOf(PropTypes.number)]),
    h: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    w: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    minHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    minWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    objectFit: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    objectPosition: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    size: PropTypes.oneOfType([PropTypes.number, PropTypes.arrayOf(PropTypes.number)]),
    boxShadow: PropTypes.string,
    paddingBottom: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    autoHeight: PropTypes.bool,
  }),
  marginSet: PropTypes.string,
};

Image.defaultProps = {
  options: {
    alt: undefined,
    aspectRatio: undefined,
    h: undefined,
    w: undefined,
    minHeight: undefined,
    minWidth: undefined,
    maxHeight: undefined,
    maxWidth: undefined,
    objectFit: 'cover',
    objectPosition: '',
    size: undefined,
    boxShadow: undefined,
    paddingBottom: undefined,
    autoHeight: false,
  },
  marginSet: '',
};

const mapState = state => ({
  screenSize: state.screenSize,
});

export default connect(mapState)(Image);



// WEBPACK FOOTER //
// ./src/components/ContentfulImage.js